import { useCallback, useEffect, useRef, useState } from "react";
import httpClient from "@/dataprovider/http-client";
import { getApiUrl } from "@/constants";
import type { ItsOneViewResponse } from "@/types/its-one-view";
import { readItsOneViewLastIts, writeItsOneViewLastIts } from "@/utils/main-dashboard-storage";

const ITS_PATTERN = /^\d{8}$/;

export function isValidEightDigitIts(itsNo: string): boolean {
  return ITS_PATTERN.test(itsNo.trim());
}

export type UseItsOneViewOptions = {
  /** When false, do not read/write Main Dashboard last-ITS session storage. Default true. */
  persistLastIts?: boolean;
  /**
   * When true, request settled (fully paid) commitments as well.
   * Dashboard One View leaves this false (pending-only).
   */
  includeSettled?: boolean;
};

/** Builds the ITS One View request URL (exported for unit tests). */
export function buildItsOneViewRequestUrl(
  apiUrl: string,
  itsNo: string,
  options?: { includeSettled?: boolean }
): string {
  const params = new URLSearchParams({ itsNo });
  if (options?.includeSettled) {
    params.set("includeSettled", "true");
  }
  return `${apiUrl}/its-one-view?${params.toString()}`;
}

export function useItsOneView(options?: UseItsOneViewOptions) {
  const persistLastIts = options?.persistLastIts !== false;
  const includeSettled = options?.includeSettled === true;
  const [data, setData] = useState<ItsOneViewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastItsNo, setLastItsNo] = useState(() => (persistLastIts ? readItsOneViewLastIts() : ""));

  const search = useCallback(
    async (itsNo: string) => {
      const trimmed = itsNo.trim();
      if (!ITS_PATTERN.test(trimmed)) {
        setError("Enter a valid 8-digit ITS number");
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const { json } = await httpClient(
          buildItsOneViewRequestUrl(getApiUrl(), trimmed, { includeSettled })
        );
        const body = json as { rows?: ItsOneViewResponse[] } & Partial<ItsOneViewResponse>;
        const row: ItsOneViewResponse | undefined = Array.isArray(body.rows)
          ? body.rows[0]
          : body.hofIts
            ? (body as ItsOneViewResponse)
            : undefined;
        if (!row?.hofIts) {
          setError("No data returned for this ITS");
          setData(null);
          return;
        }
        setData(row);
        if (persistLastIts) {
          writeItsOneViewLastIts(trimmed);
        }
        setLastItsNo(trimmed);
      } catch (e: unknown) {
        const message =
          e && typeof e === "object" && "body" in e
            ? String((e as { body?: { message?: string } }).body?.message ?? "Lookup failed")
            : "Lookup failed";
        setError(message);
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [persistLastIts, includeSettled]
  );

  const reset = useCallback(() => {
    setLoading(false);
    setData(null);
    setError(null);
    if (persistLastIts) {
      writeItsOneViewLastIts("");
    }
    setLastItsNo("");
  }, [persistLastIts]);

  return { data, loading, error, search, reset, lastItsNo };
}

/** Auto-fetch ITS One View once per ITS when `enabled` and ITS is valid. */
export function useItsOneViewForIts(
  itsNo: string | null | undefined,
  options?: UseItsOneViewOptions & { enabled?: boolean }
) {
  const enabled = options?.enabled !== false;
  const trimmed = typeof itsNo === "string" ? itsNo.trim() : "";
  const itsValid = isValidEightDigitIts(trimmed);
  const { search, ...rest } = useItsOneView(options);
  const searchedItsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !itsValid) return;
    if (searchedItsRef.current === trimmed) return;
    searchedItsRef.current = trimmed;
    void search(trimmed);
  }, [enabled, itsValid, trimmed, search]);

  return { ...rest, search, itsNo: trimmed, itsValid };
}
